import { useGet } from "@albertine/api-client";
import type { MemberStripeAccount } from "@albertine/types/openapi/lmt";
import { toAssignableAgents } from "@prototypes/agent-picker/assignableAgents";
import { AvatarResolverContext } from "@prototypes/avatar/avatarUrl";
import { LocationSearchProvider } from "@prototypes/location-picker/LocationSearchContext";
import { MemberProfile } from "@prototypes/member-profile/MemberProfile";
import {
    MemberProfileContext,
    type MemberProfileStore,
} from "@prototypes/member-profile/MemberProfileContext";
import type { ProfileOverlay } from "@prototypes/member-profile/overlays/profileOverlay";
import * as Sentry from "@sentry/react";
import { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { useAgents } from "../../common/hooks/useAgents";
import { useCitySearch } from "../../common/hooks/useCitySearch";
import { useMember } from "../../common/hooks/useMember";
import { useMemberGroups } from "../../common/hooks/useMemberGroups";
import { useMembersMap } from "../../common/hooks/useMembers";
import type { Agent } from "../../common/types/Agent";
import type { Conversation } from "../../common/types/Conversation";
import { isEligibleForReferrals } from "../../common/types/Referrals";
import { EventEmitter } from "../../EventEmitter";
import { conversationDocument, getDocumentOnce } from "../../FirebaseBackend";
import { resolveAvatarUrl } from "../../utils/avatar.util";
import { useClaims } from "../../utils/claims.util";
import { joinDisplayName } from "../../utils/name.util";
import routes from "../../utils/routes";
import SideTab from "../Common/Tab/SideTab";
import { toProfile, toProfileGroups } from "./member-profile-data.util";
import { useFindStripeCustomer } from "./useFindStripeCustomer";
import { useMemberProfileUpdates } from "./useMemberProfileUpdates";

type MemberProfileHostProps = {
    currentAgent: Agent;
    memberId: string | undefined;
};

export function MemberProfileHost({ currentAgent, memberId }: MemberProfileHostProps) {
    const history = useHistory();
    const { data: member, isError, error } = useMember(memberId);
    const [conversation, setConversation] = useState<Conversation>();
    const [overlay, setOverlay] = useState<ProfileOverlay | null>(null);

    const agents = useAgents();
    const { data: groups = [] } = useMemberGroups();
    const membersMap = useMembersMap();
    const memberApiParams = { param: { memberId: memberId ?? "" } };
    const memberQueryOptions = { enabled: !!memberId };
    const { data: membershipTiers = [] } = useGet("membershipTiers", {});
    const { data: onboarding } = useGet(
        "memberOnboardingPreferences",
        memberApiParams,
        memberQueryOptions,
    );
    const { data: preferences } = useGet(
        "memberPreferences",
        memberApiParams,
        memberQueryOptions,
    );
    const { data: paymentPrefs } = useGet(
        "memberPaymentPreferences",
        memberApiParams,
        memberQueryOptions,
    );
    const { data: stripeAccount } = useGet(
        "memberStripeAccount",
        memberApiParams,
        memberQueryOptions,
    ) as { data: MemberStripeAccount | null | undefined };
    const stripeCustomerId = stripeAccount?.stripeCustomerId;
    const { data: paymentMethods } = useGet("paymentMethods", memberApiParams, {
        enabled: memberQueryOptions.enabled && !!stripeCustomerId,
    });
    const { data: proactivity } = useGet(
        "agentProactivity",
        memberApiParams,
        memberQueryOptions,
    );
    const { data: referralsData } = useGet(
        "listMemberReferralPromotions",
        { param: { memberId: memberId ?? "" } },
        {
            enabled: Boolean(memberId && member && isEligibleForReferrals(member)),
        },
    );
    const updates = useMemberProfileUpdates(
        memberId ?? "",
        joinDisplayName(member?.firstName, member?.lastName),
    );
    const searchLocations = useCitySearch();
    const { isAdmin } = useClaims();
    const findStripeCustomer = useFindStripeCustomer(member?.tenant, isAdmin);

    useEffect(() => {
        if (!memberId) return;

        setConversation(undefined);
        setOverlay(null);
    }, [memberId]);

    useEffect(() => {
        if (!isError) return;

        Sentry.captureException(error, {
            extra: {
                message: `MemberProfileHost: get member failed for id ${memberId}`,
            },
        });
    }, [error, isError, memberId]);

    useEffect(() => {
        if (!member?.conversation) return undefined;

        let cancelled = false;
        getDocumentOnce<Conversation>(conversationDocument(member.conversation))
            .then((conversation) => {
                if (cancelled || !conversation) return;
                setConversation(conversation);
            })
            .catch((error: unknown) => Sentry.captureException(error));

        return () => {
            cancelled = true;
        };
    }, [member?.conversation]);

    if (isError) {
        return (
            <SideTab>
                <p className="p-4 text-sm text-zinc-500">
                    Could not load the member profile.
                </p>
            </SideTab>
        );
    }

    if (!member) {
        return <SideTab>{null}</SideTab>;
    }

    const agentItems = toAssignableAgents(agents, currentAgent.id, member.assignedTo);
    const profile = toProfile({
        member,
        groups,
        agents: agentItems,
        members: membersMap,
        membershipTiers,
        onboarding,
        preferences,
        conversation,
        stripe: stripeAccount,
        paymentPrefs,
        cards: paymentMethods,
        proactivity: proactivity?.items,
        referrals: referralsData?.items,
    });

    const store: MemberProfileStore = {
        profile,
        overlay,
        setOverlay,
        agents: agentItems,
        groups: toProfileGroups(groups),
        members: Object.values(membersMap).map((item) => ({
            id: item.id,
            firstName: item.firstName,
            lastName: item.lastName,
        })),
        membershipTiers,
        ...updates,
        isAdmin,
        findStripeCustomer,
        openProactivityMessage(messageId: string) {
            EventEmitter.emit({
                name: "scrollChatIntoMessage",
                params: { messageId },
            });
            history.push(routes.memberWithMessage(member.id, messageId));
        },
    };

    return (
        <SideTab>
            <AvatarResolverContext value={resolveAvatarUrl}>
                <MemberProfileContext value={store}>
                    <LocationSearchProvider value={searchLocations}>
                        <MemberProfile />
                    </LocationSearchProvider>
                </MemberProfileContext>
            </AvatarResolverContext>
        </SideTab>
    );
}
